'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Timer, X, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSiteLanguage, useUser } from '@/firebase';
import { hidesSalePromotion } from '@/lib/ad-access';
import { useActiveSale } from '@/hooks/use-active-sale';
import { GncSticker } from '@/components/gnc-sticker';
import { formatSaleTimeLeft, SALE_THEME_STYLES, type SaleEvent } from '@/lib/sale-config';

type SaleBannerText = { endsIn: string; viewDeal: string; dismiss: string; shopTheSale: string };

// Only the chrome is translated. The sale name and blurb are written by an
// admin in the Sales tab and are shown exactly as typed.
//
// viewDeal leaves for the shop; shopTheSale is for the banner already on the
// shop, where the click scrolls down to the discounted items instead.
const SALE_BANNER_TEXT: Record<string, SaleBannerText> = {
    en: { endsIn: 'Ends in', viewDeal: 'View deal', dismiss: 'Dismiss', shopTheSale: "See what's on sale" },
    he: { endsIn: 'מסתיים בעוד', viewDeal: 'לצפייה במבצע', dismiss: 'סגור', shopTheSale: 'מה במבצע' },
    es: { endsIn: 'Termina en', viewDeal: 'Ver oferta', dismiss: 'Descartar', shopTheSale: 'Ver qué está en oferta' },
    fr: { endsIn: 'Se termine dans', viewDeal: "Voir l'offre", dismiss: 'Fermer', shopTheSale: 'Voir les articles en promo' },
    de: { endsIn: 'Endet in', viewDeal: 'Angebot ansehen', dismiss: 'Schliessen', shopTheSale: 'Reduzierte Artikel ansehen' },
    it: { endsIn: 'Finisce tra', viewDeal: "Vedi l'offerta", dismiss: 'Chiudi', shopTheSale: 'Vedi cosa è in offerta' },
    ru: { endsIn: 'Закончится через', viewDeal: 'Посмотреть', dismiss: 'Закрыть', shopTheSale: 'Что со скидкой' },
    pl: { endsIn: 'Kończy się za', viewDeal: 'Zobacz ofertę', dismiss: 'Zamknij', shopTheSale: 'Zobacz przecenione' },
    ko: { endsIn: '남은 시간', viewDeal: '혜택 보기', dismiss: '닫기', shopTheSale: '할인 상품 보기' },
    ja: { endsIn: '残り', viewDeal: 'セールを見る', dismiss: '閉じる', shopTheSale: 'セール対象を見る' },
    ar: { endsIn: 'ينتهي خلال', viewDeal: 'عرض الصفقة', dismiss: 'إغلاق', shopTheSale: 'شاهد ما هو مخفض' },
    hi: { endsIn: 'समाप्त होगा', viewDeal: 'ऑफ़र देखें', dismiss: 'बंद करें', shopTheSale: 'सेल में क्या है देखें' },
    id: { endsIn: 'Berakhir dalam', viewDeal: 'Lihat penawaran', dismiss: 'Tutup', shopTheSale: 'Lihat yang diskon' },
    tr: { endsIn: 'Bitiyor', viewDeal: 'Fırsatı gör', dismiss: 'Kapat', shopTheSale: 'İndirimdekileri gör' },
    vi: { endsIn: 'Kết thúc sau', viewDeal: 'Xem ưu đãi', dismiss: 'Đóng', shopTheSale: 'Xem hàng giảm giá' },
    th: { endsIn: 'สิ้นสุดใน', viewDeal: 'ดูข้อเสนอ', dismiss: 'ปิด', shopTheSale: 'ดูสินค้าลดราคา' },
    nl: { endsIn: 'Eindigt over', viewDeal: 'Bekijk aanbieding', dismiss: 'Sluiten', shopTheSale: 'Bekijk wat in de uitverkoop is' },
    'pt-BR': { endsIn: 'Termina em', viewDeal: 'Ver oferta', dismiss: 'Fechar', shopTheSale: 'Ver o que está em promoção' },
};

export function getSaleBannerText(language?: string | null): SaleBannerText {
    return (language && SALE_BANNER_TEXT[language]) || SALE_BANNER_TEXT.en;
}

/** One dismissal per sale, so the next sale is not silently swallowed. */
const DISMISS_KEY = 'gnc_sale_banner_dismissed';

export type SaleBannerVariant = 'full' | 'compact';

interface SaleBannerViewProps {
    sale: SaleEvent;
    nowMs: number;
    variant?: SaleBannerVariant;
    className?: string;
    /** Where "View deal" goes. Defaults to the shop. */
    href?: string;
    /** Open the shop in a new tab, so a player in a lobby keeps their seat. */
    openInNewTab?: boolean;
    onDismiss?: () => void;
    /**
     * Makes the full banner clickable. For the shop, where there is nowhere to
     * navigate to: the click scrolls down to the items the sale covers.
     */
    onActivate?: () => void;
}

/**
 * Presentation only, for callers that already track the active sale (the shop
 * merges it with personal offers before deciding what to show).
 */
export function SaleBannerView({
    sale, nowMs, variant = 'compact', className, href = '/shop', openInNewTab, onDismiss, onActivate,
}: SaleBannerViewProps) {
    const { siteLanguage } = useSiteLanguage();
    const text = getSaleBannerText(siteLanguage);
    const theme = SALE_THEME_STYLES[sale.theme];
    const linkProps = openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {};

    if (variant === 'full') {
        return (
            <div
                data-testid="sale-banner-full"
                className={cn(
                    'group relative overflow-hidden rounded-3xl border-2 p-5 sm:p-6 text-left',
                    theme.bannerClass,
                    onActivate && 'transition-shadow hover:shadow-lg hover:shadow-black/30',
                    className
                )}
            >
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                    <div className="flex items-center gap-3">
                        <GncSticker name={theme.sticker} className="h-10 w-10 shrink-0 sm:h-12 sm:w-12" />
                        <div>
                            <h3 className="text-xl sm:text-2xl font-black uppercase italic tracking-tighter text-white leading-tight">
                                {sale.name}{' '}
                                <span className={theme.accentTextClass}>-{sale.percentOff}%</span>
                            </h3>
                            {sale.bannerText && (
                                <p className="text-[11px] sm:text-xs font-bold text-white/70 mt-0.5">{sale.bannerText}</p>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 self-start sm:ml-auto sm:self-auto">
                        <span className="inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-black/40 px-4 py-2">
                            <Timer className={cn('h-5 w-5', theme.accentTextClass)} />
                            <span className="whitespace-nowrap text-xs sm:text-sm font-black uppercase tracking-wide text-white tabular-nums">
                                {text.endsIn} {formatSaleTimeLeft(sale, nowMs)}
                            </span>
                        </span>
                        {onActivate && (
                            <span className="inline-flex items-center gap-1.5 rounded-2xl border border-white/30 bg-white/15 px-4 py-2 text-xs sm:text-sm font-black uppercase tracking-wide text-white transition-colors group-hover:bg-white/25">
                                {text.shopTheSale}
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </span>
                        )}
                    </div>
                </div>
                {/* Stretched hit area rather than a clickable wrapper: a <button>
                    cannot legally contain the heading, and the whole banner
                    should still be clickable, not just the cue. */}
                {onActivate && (
                    <button
                        type="button"
                        onClick={onActivate}
                        className="absolute inset-0 z-10 h-full w-full cursor-pointer rounded-3xl focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white/80"
                    >
                        <span className="sr-only">{sale.name} -{sale.percentOff}% - {text.shopTheSale}</span>
                    </button>
                )}
            </div>
        );
    }

    // Compact: one strip that fits above a lobby or a page section without
    // pushing the real content off the screen. It stacks into name-then-actions
    // on a phone; side by side the name gets squeezed into one word per line.
    return (
        <div
            data-testid="sale-banner"
            className={cn(
                'pointer-events-auto relative overflow-hidden rounded-2xl border px-3 py-2.5 sm:px-4',
                theme.bannerClass,
                className
            )}
        >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex min-w-0 items-center gap-2.5">
                    <GncSticker name={theme.sticker} className="h-7 w-7 shrink-0" />
                    <div className="min-w-0">
                        <p className="text-xs sm:text-sm font-black uppercase italic leading-tight tracking-tight text-white">
                            {sale.name} <span className={theme.accentTextClass}>-{sale.percentOff}%</span>
                        </p>
                        {sale.bannerText && (
                            <p className="mt-0.5 hidden text-[11px] font-bold text-white/70 sm:block">{sale.bannerText}</p>
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 sm:ml-auto sm:shrink-0">
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-xl border border-white/20 bg-black/40 px-2.5 py-1.5">
                        <Timer className={cn('h-4 w-4', theme.accentTextClass)} />
                        <span className="text-[11px] font-black uppercase tracking-wide text-white tabular-nums">
                            {text.endsIn} {formatSaleTimeLeft(sale, nowMs)}
                        </span>
                    </span>
                    <Link
                        href={href}
                        {...linkProps}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-xl border border-white/30 bg-white/15 px-3 py-1.5 text-[11px] font-black uppercase tracking-wide text-white transition-all hover:bg-white/25 active:scale-95"
                    >
                        {text.viewDeal}
                        <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                    {onDismiss && (
                        <button
                            type="button"
                            onClick={onDismiss}
                            aria-label={text.dismiss}
                            className="ml-auto rounded-lg p-1 text-white/40 transition-colors hover:bg-white/10 hover:text-white/80 sm:ml-0"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

interface SaleBannerProps extends Omit<SaleBannerViewProps, 'sale' | 'nowMs'> {
    /** Adds a close button whose choice sticks until the next sale starts. */
    dismissible?: boolean;
    /** Wrapper around the banner, dropped along with it when no sale is live. */
    wrapperClassName?: string;
}

/**
 * Self-contained banner: subscribes to the live sale itself and renders
 * nothing when there is none. Drop it anywhere.
 */
/** The live sale this viewer is shown, or null (none, or hidden for members). */
export function useViewerSale(tickMs?: number) {
    const { sale, nowMs } = useActiveSale(tickMs);
    const { userProfile, isUserLoading, hasAdminAccess, adminViewMode } = useUser();
    const hiddenForViewer = hidesSalePromotion({
        profile: userProfile,
        isLoading: isUserLoading,
        forceGuestPreview: hasAdminAccess && adminViewMode === 'guest',
        nowMs,
    });
    return { sale: hiddenForViewer ? null : sale, nowMs };
}

export function SaleBanner({ dismissible, wrapperClassName, ...props }: SaleBannerProps) {
    const { sale, nowMs } = useViewerSale();
    const [dismissedSaleId, setDismissedSaleId] = useState<string | null>(null);

    useEffect(() => {
        if (!dismissible) return;
        try {
            setDismissedSaleId(window.localStorage.getItem(DISMISS_KEY));
        } catch {
            // Private mode or blocked storage: the banner just stays visible.
        }
    }, [dismissible]);

    if (!sale || (dismissible && dismissedSaleId === sale.id)) return null;

    const handleDismiss = () => {
        setDismissedSaleId(sale.id);
        try {
            window.localStorage.setItem(DISMISS_KEY, sale.id);
        } catch {
            // Not persisting is fine; it is hidden for this page view either way.
        }
    };

    const banner = (
        <SaleBannerView
            {...props}
            sale={sale}
            nowMs={nowMs}
            onDismiss={dismissible ? handleDismiss : props.onDismiss}
        />
    );

    return wrapperClassName ? <div className={wrapperClassName}>{banner}</div> : banner;
}

// The shop puts the sale in its own full banner, and a lobby puts it in the
// waiting room where players actually are. On those two the site-wide strip
// would just be a second copy.
const SELF_PROMOTING_ROUTES = ['/shop', '/lobby'];

/**
 * Site-wide strip under the header, so the sale is visible on every other page
 * without each one having to opt in.
 */
export function SiteSaleStrip() {
    const pathname = usePathname();
    const isSelfPromoting = !!pathname && SELF_PROMOTING_ROUTES.some(
        (route) => pathname === route || pathname.startsWith(route + '/')
    );
    // A first-time visitor gets one overlay, not two: the strip waits until
    // the cookie banner has been answered. Returning visitors already have a
    // stored answer, so for them it shows straight away.
    const [consentAnswered, setConsentAnswered] = useState(false);
    useEffect(() => {
        const check = () => {
            try {
                setConsentAnswered(!!window.localStorage.getItem('gnc_cookie_consent'));
            } catch {
                // Blocked storage: the banner never records an answer, so show the strip.
                setConsentAnswered(true);
            }
        };
        check();
        window.addEventListener('gnc-consent-updated', check);
        return () => window.removeEventListener('gnc-consent-updated', check);
    }, []);
    if (isSelfPromoting || !consentAnswered) return null;

    // Website only: the app shows the sale as a badge on its Shop tab instead
    // (game-night-header.tsx), not a strip over the page.
    return (
        <div data-web-only>
            <SaleBanner
                dismissible
                wrapperClassName="pointer-events-none fixed inset-x-0 top-16 z-[60] mx-auto w-full max-w-6xl px-3 pt-3 sm:px-4 md:top-20"
            />
        </div>
    );
}
